import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary, H3, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { RatingDisplaySmall } from '@/components/RatingDisplaySmall/RatingDisplaySmall';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import { ReviewComment, ReviewResponse } from '@/types/Review';
import { Seller } from '@/types/Seller';
import { getBidderReviewLocation } from '@/utils/address';
import { shortFormatDate } from '@/utils/dates';
import { getImageUrl } from '@/utils/urls';

import { ReviewAndComments } from './ReviewAndComments';

export type ReviewProps = {
    comments: ReviewComment[];
    helpfulCount: number;
    houseReview: ReviewResponse;
    seller: Seller;
    statuses: string[];
    submitted: boolean;
};

const getDisplayTime = (time: string) => shortFormatDate(new Date(time));

export const Review = ({ comments, helpfulCount, houseReview, seller, statuses, submitted }: ReviewProps) => {
    const mobile = useIsMobileLayout();

    const {
        bidderCity,
        bidderCountry,
        bidderFirstName,
        bidderState,
        catalogId,
        overall: rating,
        review,
        reviewHeadline,
        reviewId,
        reviewSubmitted,
        wonItems,
    } = houseReview;

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);

    const imageSize = !mobile ? 94 : 67;
    const imageCount = !mobile ? 3 : 1;

    const images = wonItems
        ? wonItems.slice(0, imageCount).map((item, index) => {
              return (
                  <img
                      alt={item.title}
                      key={index}
                      loading="lazy"
                      src={getImageUrl({
                          catalogId: catalogId,
                          height: imageSize,
                          itemId: item.itemId,
                          sellerId: seller.sellerId,
                      })}
                  />
              );
          })
        : [];

    const imageView = (
        <div
            className={cn('flex items-center justify-center border border-border-secondary', {
                'absolute right-0 h-[69px] w-[69px] [&>img]:max-h-[67px] [&>img]:max-w-[67px]': mobile,
                'h-[94px] w-[94px] text-center [&>img]:m-0 [&>img]:max-h-full [&>img]:max-w-full': !mobile,
            })}
        >
            {images}
        </div>
    );

    const bidder = (
        <div
            className={cn({
                '[&>div:first-child]:inline-block': mobile,
                'mb-xs flex flex-wrap': !mobile,
            })}
        >
            <H3 className="flex flex-wrap items-center">
                {bidderFirstName}
                <Info
                    className="mx-2xs -mt-2xs inline-block"
                    color="secondary"
                    customStyles={{ color: isWhiteLabel ? whiteLabelPrimaryColor : 'inherit' }}
                    id="review-verified-buyer-trigger"
                    size="sm"
                    style="fill"
                />
                <Tooltip
                    anchorSelect="#review-verified-buyer-trigger"
                    place="bottom"
                >
                    <LabelStrong>
                        <FormattedMessage id="review.verified_buyer_headline" />
                    </LabelStrong>
                    <BodySecondary>
                        <FormattedMessage id="review.verified_buyer_text" />
                    </BodySecondary>
                </Tooltip>
                <BodySecondary
                    className={cn('tracking-normal tabletMax:ml-xs')}
                    color="secondary"
                >
                    {getBidderReviewLocation(bidderCity, bidderState, bidderCountry)}
                </BodySecondary>
            </H3>
        </div>
    );

    const ratingsAndDate = (
        <div className="align-center flex gap-xs">
            <div>
                <RatingDisplaySmall
                    hideReviewCount
                    rating={rating}
                    small
                />
            </div>
            <div>
                <BodyPrimary color="secondary">{getDisplayTime(reviewSubmitted)}</BodyPrimary>
            </div>
        </div>
    );

    const reviewMessaging = (
        <ReviewAndComments
            comments={comments}
            helpfulCount={helpfulCount}
            review={review}
            reviewHeadline={reviewHeadline}
            reviewId={reviewId}
            seller={seller}
            statuses={statuses}
            submitted={submitted}
        />
    );

    return mobile ? (
        <div className="mt-md flex min-h-[140px] flex-col justify-start border-b border-border-secondary pb-md">
            {imageView}
            <div className="flex">
                {bidder}
                {ratingsAndDate}
            </div>
            {reviewMessaging}
        </div>
    ) : (
        <div
            className="mt-md flex min-h-[140px] justify-start border-b border-border-secondary pb-md"
            data-testid="catalogDesktopReviewCard"
        >
            <div className="flex w-[132px] min-w-[132px] flex-col justify-start">{bidder}</div>
            <div className="mr-xs ml-2xl flex w-4/5 flex-col justify-start smMax:ml-lg">
                {ratingsAndDate}
                {reviewMessaging}
            </div>
            {imageView}
        </div>
    );
};
